import React, { useMemo, useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CLI_AUTH } from '@test-kit/selectors/cli-auth';
import { auth } from '../../firebase';
import {
  buildCliCallbackUrl,
  readCliAuthParams,
  requestCliAuthorization,
} from './cliAuthPageUtils';

const BUILDER_API_URL = import.meta.env.VITE_BUILDER_API_URL || '';

function CliAuthLoginPage() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const authParams = useMemo(() => readCliAuthParams(location.search), [location.search]);
  const isBusy = status === 'signing-in' || status === 'authorizing' || status === 'redirecting';

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!authParams.ok || isBusy) {
      return;
    }

    setStatus('signing-in');
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      setStatus('authorizing');
      const authorization = await requestCliAuthorization({
        currentUser: userCredential.user,
        authParams,
        apiBaseUrl: BUILDER_API_URL,
      });

      setStatus('redirecting');
      window.location.assign(buildCliCallbackUrl(authParams.redirectUri, authorization.code, authParams.state));
    } catch (err) {
      setStatus('idle');
      setError(err instanceof Error ? err.message : 'CLI sign in failed.');
    }
  };

  if (!authParams.ok) {
    return (
      <CliAuthLoginShell>
        <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
          CLI authorization failed
        </Typography>
        <Typography color="error" data-testid={CLI_AUTH.error}>
          {authParams.error}
        </Typography>
      </CliAuthLoginShell>
    );
  }

  return (
    <CliAuthLoginShell>
      <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
        Sign in to authorize Segmently CLI
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }} data-testid={CLI_AUTH.status}>
        {status === 'redirecting' ? 'Returning to the CLI...' : 'This sign in is used only for CLI authorization.'}
      </Typography>
      {error && (
        <Typography color="error" sx={{ mb: 2 }} data-testid={CLI_AUTH.error}>
          {error}
        </Typography>
      )}
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, textAlign: 'left' }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={isBusy}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="password"
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={isBusy}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3 }}
          disabled={isBusy}
        >
          {isBusy ? <CircularProgress size={20} color="inherit" /> : 'Sign in and authorize'}
        </Button>
      </Box>
    </CliAuthLoginShell>
  );
}

function CliAuthLoginShell({ children }) {
  return (
    <Box
      sx={{
        minHeight: '100svh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
      }}
      data-testid={CLI_AUTH.page}
    >
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          {children}
        </Paper>
      </Container>
    </Box>
  );
}

export default CliAuthLoginPage;
